/**
 * Auth helper — reads Telegram initData, calls POST /api/auth,
 * stores result in module-level state (single-tab lifetime is fine for a Mini App).
 */

import { initDataRaw } from "@telegram-apps/sdk-react";

export interface AuthResult {
  lang: "uk" | "en";
  is_member: boolean;
  is_admin: boolean;
}

let _initDataRaw: string = "";
let _authResult: AuthResult | null = null;

/** Call once at app start. Returns cached result on repeat calls. */
export async function authenticate(baseUrl: string): Promise<AuthResult> {
  if (_authResult) return _authResult;

  // Safely read initDataRaw signal — may be undefined outside Telegram
  try {
    _initDataRaw = initDataRaw() ?? "";
  } catch {
    _initDataRaw = "";
  }
  // Dev-only: outside Telegram the SDK provides no initData. Allow a signed
  // test initData via VITE_DEV_INIT_DATA so the admin flow can be exercised locally.
  if (!_initDataRaw) {
    _initDataRaw = ((import.meta as any).env?.VITE_DEV_INIT_DATA as string) ?? "";
  }

  try {
    const res = await fetch(`${baseUrl}/api/auth`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(
          _initDataRaw
            ? { Authorization: `tma ${_initDataRaw}` }
            : {}
        ),
      },
    });

    if (!res.ok) throw new Error(`/api/auth returned ${res.status}`);
    const data = (await res.json()) as AuthResult;
    _authResult = data;
    return data;
  } catch (e) {
    console.warn("[auth] Falling back to anonymous:", e);
    // Outside Telegram or backend not available — anonymous defaults
    const lang = navigator.language.startsWith("uk") ? "uk" : "en";
    _authResult = { lang: lang as "uk" | "en", is_member: false, is_admin: false };
    return _authResult;
  }
}

/** Raw Telegram init data string (empty outside Telegram). */
export function getInitDataRaw(): string {
  return _initDataRaw;
}

export interface TelegramUser {
  id?: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
}

/**
 * Parse Telegram user object from initData string.
 * Works in dev when VITE_DEV_INIT_DATA carries a user= param.
 */
export function getUser(): TelegramUser | null {
  const raw = _initDataRaw;
  if (!raw) return null;
  try {
    const params = new URLSearchParams(raw);
    const userStr = params.get("user");
    if (!userStr) return null;
    return JSON.parse(decodeURIComponent(userStr)) as TelegramUser;
  } catch {
    return null;
  }
}

/** Cached auth result — null until authenticate() has been called. */
export function getCachedAuth(): AuthResult | null {
  return _authResult;
}
